import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { PopoverModule } from 'primeng/popover';
import { InputNumberModule } from 'primeng/inputnumber';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { ChartOptionsConfig, ChartOptionsStorageService, ChartYAxisMode } from './chart-options-storage.service';

const DEFAULT_CONFIG: ChartOptionsConfig = { yAxisMode: 'auto-data' };

/**
 * Small "..." button + popover for per-chart display preferences (Y-axis
 * mode/range, X-axis same-origin). Opt-in via [chartId] — a chart component
 * that doesn't pass one simply doesn't get the button. Persists to
 * localStorage via ChartOptionsStorageService so choices survive across
 * sessions.
 *
 * The host chart component owns applying the emitted config to its own
 * chart.js options (this component has no chart.js knowledge itself) — see
 * MultiLineChartComponent's use of (configChange).
 */
@Component({
  selector: 'cosmos-chart-options-button',
  imports: [FormsModule, TranslatePipe, PopoverModule, InputNumberModule, RadioButtonModule, ButtonModule, CheckboxModule, DividerModule],
  templateUrl: './chart-options-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartOptionsButtonComponent implements OnInit {
  private readonly storage = inject(ChartOptionsStorageService);

  /** Storage key + identity for this chart, e.g. "shm_icorr". Required. */
  readonly chartId = input.required<string>();

  /** Whether this chart has threshold bands configured — gates the
   *  "auto-bands" radio option (hidden entirely when false, per design). */
  readonly hasBands = input<boolean>(false);

  /** Whether this chart supports the relative-time X axis — gates the
   *  X-axis section (hidden entirely when false, mirrors hasBands). */
  readonly sameOriginAllowed = input<boolean>(false);

  /** Emits whenever the resolved config changes — on init (if a stored
   *  override exists) and on every user edit in the popover. The host chart
   *  applies this to its live chart.js instance immediately. */
  readonly configChange = output<ChartOptionsConfig>();

  /** Overlay (right-axis) section — shown only while an overlay is active. Its range is
   *  deliberately NOT persisted: the overlay choice itself isn't, so a stored range would
   *  outlive the variable it belongs to. */
  readonly overlayKey = input<string | null>(null);
  /** Headings for the two axes, e.g. "Strength (MPa)" / "Temperature (ºC)". */
  readonly primaryAxisText = input<string | null>(null);
  readonly overlayAxisText = input<string | null>(null);
  /** Emits the right-axis range config (auto-data | manual + min/max) on every edit. */
  readonly overlayAxisChange = output<ChartOptionsConfig>();

  protected readonly y2Mode = signal<ChartYAxisMode>('auto-data');
  protected readonly y2MinDraft = signal<number | null>(null);
  protected readonly y2MaxDraft = signal<number | null>(null);
  protected readonly y2Incomplete = computed(() =>
    this.y2Mode() === 'manual' && (this.y2MinDraft() == null || this.y2MaxDraft() == null),
  );

  constructor() {
    // A different (or removed) overlay variable starts back at auto; the chart resets its own copy too.
    effect(() => {
      this.overlayKey();
      untracked(() => {
        this.y2Mode.set('auto-data');
        this.y2MinDraft.set(null);
        this.y2MaxDraft.set(null);
      });
    });
  }

  protected readonly config = signal<ChartOptionsConfig>(DEFAULT_CONFIG);

  protected readonly yMinDraft = signal<number | null>(null);
  protected readonly yMaxDraft = signal<number | null>(null);

  protected readonly manualRangeIncomplete = computed(() =>
    this.config().yAxisMode === 'manual' && (this.yMinDraft() == null || this.yMaxDraft() == null),
  );

  ngOnInit(): void {
    const stored = this.storage.get(this.chartId());
    const resolved = stored ?? DEFAULT_CONFIG;
    this.config.set(resolved);
    this.yMinDraft.set(resolved.yMin ?? null);
    this.yMaxDraft.set(resolved.yMax ?? null);
    this.configChange.emit(resolved);
  }

  protected onModeChange(mode: ChartYAxisMode): void {
    // Switching to 'manual' with no min/max entered yet (first time) would
    // otherwise apply {min: undefined, max: undefined} immediately — wait
    // for onManualRangeChange() to have both values before applying/persisting.
    if (mode === 'manual' && (this.yMinDraft() == null || this.yMaxDraft() == null)) {
      this.config.update((c) => ({ ...c, yAxisMode: mode }));
      return;
    }
    const next: ChartOptionsConfig = { ...this.config(), yAxisMode: mode };
    this.applyAndPersist(next);
  }

  protected onManualRangeChange(): void {
    const min = this.yMinDraft();
    const max = this.yMaxDraft();
    if (min == null || max == null) return; // wait for both fields
    this.applyAndPersist({ ...this.config(), yAxisMode: 'manual', yMin: min, yMax: max });
  }

  protected onOverlayModeChange(mode: ChartYAxisMode): void {
    this.y2Mode.set(mode);
    if (mode === 'auto-data') this.overlayAxisChange.emit({ yAxisMode: 'auto-data' });
    else this.onOverlayRangeChange();
  }

  protected onOverlayRangeChange(): void {
    const min = this.y2MinDraft();
    const max = this.y2MaxDraft();
    if (min == null || max == null) return; // wait for both fields
    this.overlayAxisChange.emit({ yAxisMode: 'manual', yMin: min, yMax: max });
  }

  protected onSameOriginChange(sameOrigin: boolean): void {
    this.applyAndPersist({ ...this.config(), sameOrigin });
  }

  private applyAndPersist(next: ChartOptionsConfig): void {
    this.config.set(next);
    this.storage.set(this.chartId(), next);
    this.configChange.emit(next);
  }
}
